import { useState, useMemo, useRef, useEffect } from 'react';
import { FormikErrors, useField, useFormikContext, FieldArray } from 'formik';
import { useTranslation } from 'react-i18next';
import { Container, Row, Col, Card, Button } from 'react-bootstrap';
import { FormTextField, Checkbox, ReactSelectForm } from '../../../../../components';
import { Class, Court } from '../../../../../types/tournament';
import { CustomValueCheckbox } from './CustomValueCheckbox';
import { ROUND_KEYS } from '../schedule.helpers';

type OptInCheckBoxArrayProps = {
  dayKey: number;
} & (OptInClassProps | OptInCourtProps);

type OptInClassProps = {
  name: 'class';
  data: Class[];
  courts: Court[];
};

type OptInCourtProps = {
  name: 'court';
  data: Court[];
};

const ClassSettingsBlock = ({ classObj, title }: { classObj: Class; title: string }) => {
  const { t } = useTranslation();
  const prefix = 'creativeDetail-';
  const { values } = useFormikContext<any>();

  const priorityOptions = useMemo(
    () => [
      { label: t(prefix + 'useGlobalDefault'), value: '' },
      { label: t(prefix + 'priorityHigh'), value: 1 },
      { label: t(prefix + 'priorityNormal'), value: 30 },
      { label: t(prefix + 'priorityLow'), value: 60 },
    ],
    [t, prefix]
  );

  const roundOptions = useMemo(() => {
    return ROUND_KEYS.map((rk) => ({ label: rk.label, value: rk.key }));
  }, []);

  const customDurations = values?.classOverrides?.[classObj.id]?.customDurations || [];

  return (
    <div className="p-3 mb-4 bg-white border rounded shadow-sm">
      <h6 className="fw-bold mb-3 text-primary">{title} Overrides</h6>

      <Row className="mb-3 border-bottom pb-3">
        <Col md={6} className="mb-2">
          <label className="form-label small fw-bold text-muted mb-1">
            {t(prefix + 'schedulingPriorityLabel')}
          </label>
          <ReactSelectForm
            name={`classOverrides.${classObj.id}.event_priority`}
            options={priorityOptions}
            placeholder={t(prefix + 'useGlobalDefault')}
          />
        </Col>
        <Col md={6} className="mb-2">
          <FormTextField
            type="number"
            name={`classOverrides.${classObj.id}.span_limit`}
            label={t(prefix + 'maxSpanLimitLabel')}
            placeholder={t(prefix + 'globalDefaultPlaceholder')}
          />
        </Col>
      </Row>

      <Row className="mb-3 border-bottom pb-3">
        <Col md={4} className="mb-2">
          <FormTextField
            type="number"
            name={`classOverrides.${classObj.id}.gap_group_group`}
            label={t(prefix + 'minGapGroupGroup')}
            placeholder={t(prefix + 'globalPlaceholder')}
          />
        </Col>
        <Col md={4} className="mb-2">
          <FormTextField
            type="number"
            name={`classOverrides.${classObj.id}.gap_group_ko`}
            label={t(prefix + 'minGapGroupKo')}
            placeholder={t(prefix + 'globalPlaceholder')}
          />
        </Col>
        <Col md={4} className="mb-2">
          <FormTextField
            type="number"
            name={`classOverrides.${classObj.id}.gap_ko_ko`}
            label={t(prefix + 'minGapKoKo')}
            placeholder={t(prefix + 'globalPlaceholder')}
          />
        </Col>
      </Row>

      <h6 className="fw-bold mb-2 text-dark">Match Duration Overrides (Minutes)</h6>
      <FieldArray name={`classOverrides.${classObj.id}.customDurations`}>
        {({ push, remove }) => (
          <div>
            {customDurations.map((item: any, index: number) => (
              <Row key={index} className="align-items-center mb-2">
                <Col md={6}>
                  <ReactSelectForm
                    name={`classOverrides.${classObj.id}.customDurations.${index}.roundKey`}
                    options={roundOptions}
                    placeholder="Select Round"
                  />
                </Col>
                <Col md={4}>
                  <FormTextField
                    type="number"
                    name={`classOverrides.${classObj.id}.customDurations.${index}.duration`}
                    placeholder="Duration (e.g. 30)"
                  />
                </Col>
                <Col md={2}>
                  <Button variant="outline-danger" size="sm" onClick={() => remove(index)}>
                    Remove
                  </Button>
                </Col>
              </Row>
            ))}
            <Button
              variant="outline-primary"
              size="sm"
              className="mt-2"
              onClick={() => push({ roundKey: '', duration: null })}
            >
              + Add Round Duration
            </Button>
          </div>
        )}
      </FieldArray>
    </div>
  );
};

export const OptInCheckBoxArray = (props: OptInCheckBoxArrayProps) => {
  const { name, dayKey, data } = props;
  const { t } = useTranslation();
  const prefix = 'creativeDetail-';

  const allOptionsFieldId = `all-${name}-${dayKey}`;
  const optInsGroupFieldId = `${name}-day-${dayKey}`;

  const { setFieldValue } = useFormikContext<any>();
  const [allEntitiesField] = useField(allOptionsFieldId);
  const [optInEntitiesField] = useField(optInsGroupFieldId);
  const [collapsedItems, setCollapsedItems] = useState<Record<string, boolean>>({});

  const isFirstRender = useRef(true);
  const prevAllValue = useRef(allEntitiesField.value);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    if (prevAllValue.current === true && allEntitiesField.value === false) {
      setFieldValue(optInsGroupFieldId, []);
    }

    prevAllValue.current = allEntitiesField.value;
  }, [allEntitiesField.value, optInsGroupFieldId, setFieldValue]);

  const toggleCollapse = (id: string) => {
    setCollapsedItems((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const checkedValues = Array.isArray(optInEntitiesField.value) ? optInEntitiesField.value : [];

  const parentClasses = useMemo(() => {
    if (name !== 'class') return [];
    return data.filter((c) => !c.parentClassId);
  }, [name, data]);

  return (
    <Container fluid className="px-0">
      <h4 className="mb-3 text-capitalize">{t(prefix + name + 'Title')}</h4>

      <div className="d-flex align-items-center p-3 mb-3 bg-light border rounded">
        <div className="me-3">
          <Checkbox name={allOptionsFieldId} />
        </div>
        <div className="fw-bold fs-5">{t(prefix + name + 'OptIn')}</div>
      </div>

      {!allEntitiesField.value && name === 'court' && (
        <Row>
          {data.map((elem) => (
            <Col md={4} sm={6} className="mb-2" key={`court-${elem.id}`}>
              <div className="d-flex align-items-center p-2 border rounded bg-white">
                <div className="me-3">
                  <CustomValueCheckbox
                    name={optInsGroupFieldId}
                    value={elem.id.toString()}
                    dayKey={dayKey}
                  />
                </div>
                <div className="fs-6">{elem.name}</div>
              </div>
            </Col>
          ))}
        </Row>
      )}

      {!allEntitiesField.value && name === 'class' && (
        <Row>
          {parentClasses.map((elem) => {
            const elemIdStr = elem.id.toString();
            const isChecked = checkedValues.includes(elemIdStr);
            const isExpanded = isChecked && !collapsedItems[elemIdStr];
            const childClasses = data.filter((c) => c.parentClassId === elem.id);

            return (
              <Col xs={12} className="mb-3" key={`class-${elem.id}`}>
                <Card className={`shadow-sm ${isChecked ? 'border-primary' : ''}`}>
                  <Card.Header className="bg-white d-flex align-items-center justify-content-between py-3">
                    <div className="d-flex align-items-center">
                      <div className="me-3">
                        <CustomValueCheckbox
                          name={optInsGroupFieldId}
                          value={elemIdStr}
                          dayKey={dayKey}
                        />
                      </div>
                      <h5 className="mb-0 fw-bold">{elem.name}</h5>
                    </div>
                    {isChecked && (
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={() => toggleCollapse(elemIdStr)}
                      >
                        {isExpanded ? t(prefix + 'collapseSettings') : t(prefix + 'expandSettings')}
                      </Button>
                    )}
                  </Card.Header>

                  {isChecked && !isExpanded && (
                    <Card.Body className="bg-light border-top">
                      <ClassSettingsBlock classObj={elem} title="Main Category" />

                      {childClasses.length > 0 && (
                        <div className="mt-4 pt-4 border-top">
                          <h5 className="fw-bold mb-3 text-dark">Sub-Categories</h5>
                          {childClasses.map((child) => (
                            <ClassSettingsBlock
                              key={child.id}
                              classObj={child}
                              title={child.name}
                            />
                          ))}
                        </div>
                      )}
                    </Card.Body>
                  )}
                </Card>
              </Col>
            );
          })}
        </Row>
      )}
    </Container>
  );
};

interface OptInErrorProps {
  errors: FormikErrors<any>;
}

export const OptInError = ({ errors }: OptInErrorProps) => {
  return (
    <>
      {Object.keys(errors).map((key) => {
        if (key.startsWith('all-court-') || key.startsWith('all-class-')) {
          return <p className="text-danger small mt-1" key={key}>{`${errors[key]}`}</p>;
        }
        return null;
      })}
    </>
  );
};